import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/eoffice/Layout";
import RichEditor from "@/components/eoffice/RichEditor";
import api, { formatApiErrorDetail, docPreviewUrl, API_BASE } from "@/lib/api";
import { safeHtml } from "@/lib/sanitize";
import { EDITOR } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Undo, CheckCircle2, FileDown, Files, PanelRightOpen, Clock, X, Download, MessageSquare, Check, Trash2, CornerUpLeft, Paperclip, Pencil } from "lucide-react";import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

function fmt(dt) {
  try {
    const d = new Date(dt);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  } catch { return dt; }
}

export default function FileEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activeHtml, setActiveHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState("note");
  const [toolbarSlot, setToolbarSlot] = useState(null);
  const [splitDocId, setSplitDocId] = useState(null);
  const [splitDocPage, setSplitDocPage] = useState(1);
  const [splitTarget, setSplitTarget] = useState(null);  // { type: 'doc'|'draft', id }
  useEffect(() => { setMobilePane(splitOpen ? "doc" : "note"); }, [splitOpen, splitTarget]);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftHtml, setDraftHtml] = useState("");
  const [draftSaveState, setDraftSaveState] = useState("saved");  // saved | dirty | saving
  const [splitUploadOpen, setSplitUploadOpen] = useState(false);
  const [splitUploadFile, setSplitUploadFile] = useState(null);
  const [splitUploadTitle, setSplitUploadTitle] = useState("");
  const [splitUploadBusy, setSplitUploadBusy] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [newComment, setNewComment] = useState("");
  const [commentBlockId, setCommentBlockId] = useState(null); // null = file-level
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, u, d] = await Promise.all([
        api.get(`/files/${id}`),
        api.get("/users"),
        api.get("/documents"),
      ]);
      setFile(f.data);
      setUsers(u.data);
      setDocuments(d.data);
      setSelectedTargets(f.data.attached_docs || []);
      const mine = (f.data.blocks || []).find((b) => b.author_id === user?.id && !b.finalized);
      const initialHtml = mine ? mine.html : "";
      setActiveHtml(initialHtml);
      // Seed the "last saved" marker so the debounce doesn't fire a wasted
      // no-op autosave right after the file loads.
      lastSavedHtml.current = initialHtml;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load file");
      nav("/");
    }
  }, [id, user?.id, nav]);

  useEffect(() => { load(); }, [load]);

  const isHolder = file && file.current_holder_id === user?.id;
  const isCreator = file && file.creator_id === user?.id;
  const canEdit = file && !file.approved && isHolder;
  const canApprove = file && !file.approved && isCreator && isHolder;
  const canDelete = file && !file.approved && isCreator
    && file.current_holder_id === user?.id
    && file.status === "draft";
  const canRevoke = (() => {
    if (!file || file.approved) return false;
    const sents = (file.history || []).filter((h) => h.type === "sent");
    if (!sents.length) return false;
    const last = sents[sents.length - 1];
    return last.actor_id === user?.id && !file.opened_by_recipient && file.current_holder_id !== user?.id;
  })();

  // Most recent person who sent this file to the current user — the "previous sender"
  // used by the quick Send Back action.
  const previousSender = useMemo(() => {
    if (!file || !user?.id) return null;
    const hist = file.history || [];
    for (let i = hist.length - 1; i >= 0; i--) {
      const h = hist[i];
      if (h.type === "sent" && h.target_id === user.id && h.actor_id !== user.id) {
        return { id: h.actor_id, name: h.actor_name, designation: h.actor_designation };
      }
    }
    return null;
  }, [file, user?.id]);
  const canSendBack = !!(canEdit && previousSender);

  const timer = useRef(null);
  const lastSavedHtml = useRef(null);
  useEffect(() => {
    if (!canEdit) return;
    // Skip autosave when the content hasn't actually changed since the last
    // successful save. Without this the debounce fires on every focus/blur
    // cycle and re-ships the entire (potentially large) HTML payload.
    if (activeHtml === lastSavedHtml.current) return;
    if (timer.current) clearTimeout(timer.current);
    // 2.5 s debounce — big enough that a burst of typing coalesces into one
    // round-trip, small enough that the user doesn't lose more than one
    // sentence if the tab crashes.
    timer.current = setTimeout(() => saveBlock(activeHtml, true), 2500);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [activeHtml, canEdit]);

  const saveBlock = async (html, silent = false) => {
    if (!canEdit) return;
    if (html === lastSavedHtml.current) {
      // Nothing new to persist — bail out early to avoid a wasted round-trip.
      if (!silent) toast.success("Saved");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/files/${id}/block`, { html });
      lastSavedHtml.current = html;
      setLastSaved(new Date());
      if (!silent) toast.success("Saved");
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!sendTo) { toast.error("Select a recipient"); return; }
    try {
      await saveBlock(activeHtml, true);
      await api.post(`/files/${id}/send`, { to_user_id: sendTo, message: sendMsg });
      toast.success("File sent");
      setSendOpen(false); setSendTo(""); setSendMsg("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSendBack = () => {
    if (!previousSender) { toast.error("No previous sender found"); return; }
    setSendTo(previousSender.id);
    setSendMsg("");
    setSendOpen(true);
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this file? The recipient will lose access.")) return;
    try {
      await api.post(`/files/${id}/revoke`);
      toast.success("File revoked");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [closeOpen, setCloseOpen] = useState(false);
  const [promoteDraftIds, setPromoteDraftIds] = useState([]);
  const openCloseDialog = () => {
    setPromoteDraftIds((file?.file_drafts || []).map((d) => d.id));
    setCloseOpen(true);
  };
  const approve = async () => {
    try {
      for (const did of promoteDraftIds) {
        try { await api.post(`/files/${id}/drafts/${did}/promote`); }
        catch (e) { console.error("promote failed", did, e); }
      }
      await api.post(`/files/${id}/approve`);
      toast.success("File closed");
      setCloseOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteFile = async () => {
    if (!window.confirm(`Permanently delete the file "${file.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("File deleted");
      nav("/");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAttached = async () => {
    try {
      await api.post(`/files/${id}/attach-docs`, { document_ids: selectedTargets });
      toast.success("Supporting documents updated");
      setAttachOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/files/${id}/comments`, { text: newComment.trim(), block_id: commentBlockId });
      setNewComment("");
      toast.success("Comment added");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleResolve = async (cid, current) => {
    try {
      await api.patch(`/files/${id}/comments/${cid}?resolved=${!current}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/files/${id}/comments/${cid}`);
      toast.success("Comment deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportPdf = async (mode = "single") => {
    setExporting(true);
    try {
      const token = localStorage.getItem("eo_token") || "";
      const path = mode === "bundle" ? "pdf-bundle" : "pdf";
      const url = `${API_BASE}/files/${id}/${path}?token=${encodeURIComponent(token)}`;
      window.open(url, "_blank");
      setExportOpen(false);
    } catch (e) { toast.error("Could not export PDF"); }
    finally { setExporting(false); }
  };

  const attachedCount = (file?.attached_docs || []).length;
  const canBundle = attachedCount > 0;

  const onExportClick = () => {
    // Closed file WITH attachments → let the user pick single vs bundle.
    // Any other case (draft or no attachments) → just export the single PDF directly.
    if (canBundle) setExportOpen(true);
    else exportPdf("single");
  };

  const splitUpload = async () => {
    if (!splitUploadFile) { toast.error("Choose a file to upload"); return; }
    const title = (splitUploadTitle || splitUploadFile.name).trim();
    if (!title) { toast.error("Title is required"); return; }
    setSplitUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", splitUploadFile);
      fd.append("title", title);
      fd.append("description", "");
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const nextAttached = Array.from(new Set([...(file.attached_docs || []), data.id]));
      await api.post(`/files/${id}/attach-docs`, { document_ids: nextAttached });
      toast.success("Uploaded and attached");
      setSplitUploadOpen(false);
      setSplitUploadFile(null);
      setSplitUploadTitle("");
      await load();
      setSplitDocId(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSplitUploadBusy(false); }
  };

  const attachedDocs = useMemo(
    () => documents.filter((d) => (file?.attached_docs || []).includes(d.id)),
    [documents, file]
  );

  // Docs attached DURING a given block (session), keyed by block id. Prefers
  // the `block_id` stamp persisted on each attachment_meta entry (see
  // backend POST /attach-docs); for legacy entries without a stamp, falls
  // back to the same timestamp-window heuristic the server uses so old files
  // still render correctly on-screen.
  const docsByBlock = useMemo(() => {
    if (!file) return {};
    const meta = file.attachment_meta || [];
    const blocks = file.blocks || [];
    const metaByDoc = new Map(meta.map((m) => [m.doc_id, m]));
    // Mirror of _pick_block_id_for_attachment in backend/server.py
    const pickBlockId = (attachedBy, attachedAt) => {
      if (!attachedBy) return null;
      const authorBlocks = blocks
        .filter((b) => b.author_id === attachedBy)
        .slice()
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      if (!authorBlocks.length) return null;
      if (!attachedAt) return authorBlocks[0].id;
      for (const b of authorBlocks) {
        const start = b.created_at || "";
        const end = b.finalized_at || "9999-12-31T23:59:59Z";
        if (start <= attachedAt && attachedAt <= end) return b.id;
      }
      const earlier = authorBlocks.filter((b) => (b.created_at || "") <= attachedAt);
      return (earlier.length ? earlier[earlier.length - 1] : authorBlocks[0]).id;
    };
    const map = {};
    (file.attached_docs || []).forEach((did) => {
      const m = metaByDoc.get(did) || {};
      let bid = m.block_id;
      if (!bid) {
        bid = pickBlockId(
          m.attached_by || file.creator_id,
          m.attached_at || file.created_at,
        );
      }
      if (!bid) return;
      if (!map[bid]) map[bid] = [];
      map[bid].push(did);
    });
    return map;
  }, [file]);

  const fileDrafts = useMemo(() => file?.file_drafts || [], [file]);
  const currentDraft = useMemo(
    () => splitTarget?.type === "draft" ? fileDrafts.find((d) => d.id === splitTarget.id) : null,
    [splitTarget, fileDrafts]
  );

  // Load a draft into the editor state when the selection changes.
  useEffect(() => {
    if (currentDraft) {
      setDraftTitle(currentDraft.title || "");
      setDraftHtml(currentDraft.html || "");
      setDraftSaveState("saved");
    }
  }, [currentDraft?.id]);

  // Auto-save the draft ~2.5s after the user stops typing (matches note-block
  // debounce). Also skips the `load()` refetch after save — reloading the
  // full file (with all its blocks/history/attachments) on every keystroke
  // silently kills perf as the file grows.
  useEffect(() => {
    if (!currentDraft || draftSaveState !== "dirty") return;
    const t = setTimeout(async () => {
      setDraftSaveState("saving");
      try {
        await api.patch(`/files/${id}/drafts/${currentDraft.id}`, { title: draftTitle, html: draftHtml });
        setDraftSaveState("saved");
      } catch (e) {
        setDraftSaveState("dirty");
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [draftTitle, draftHtml, draftSaveState, currentDraft?.id, id]);

  const createDraft = async () => {
    try {
      const { data } = await api.post(`/files/${id}/drafts`, { title: "Untitled draft", html: "" });
      toast.success("Draft document created");
      await load();
      setSplitTarget({ type: "draft", id: data.id });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteDraft = async (dId) => {
    if (!window.confirm("Delete this draft document? This cannot be undone.")) return;
    try {
      await api.delete(`/files/${id}/drafts/${dId}`);
      toast.success("Draft deleted");
      if (splitTarget?.id === dId) setSplitTarget(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openDocInSplit = useCallback((docId, page = 1) => {
    setSplitDocId(docId);
    setSplitDocPage(page);
    setSplitTarget({ type: "doc", id: docId });
    setSplitOpen(true);
  }, []);

  // Number of already-finalized (non-empty) notes — used by the editor's link picker
  const finalizedNoteCount = useMemo(() => {
    if (!file) return 0;
    return (file.blocks || []).filter((b) => b.finalized && (b.html || "").replace(/<[^>]*>/g, "").trim() !== "").length;
  }, [file]);

  // Click handler for in-document hyperlinks. New format:
  //   • Note link:  <a href="#" data-eo-note="N">
  //   • Doc link:   <a href="#" data-eo-doc="{docId}" data-eo-page="{N}">
  // Legacy fragment-based links (#note-N, #doc:{id}:page=N) are still recognized
  // for backwards compatibility with content authored before the fix.
  useEffect(() => {
    const handler = (e) => {
      // The clickable can now be either a <span role="link"> (new bulletproof
      // format that carries data-eo-* attrs) or a legacy <a href="#note/doc">.
      const el = e.target && e.target.closest && e.target.closest(
        "span[data-eo-doc], span[data-eo-note], span[data-eo-draft], a[href]"
      );
      if (!el) return;
      if (!el.closest("[data-eo-doc-panel='1']")) return;

      // 1) New data-attribute format (bulletproof)
      const noteAttr = el.getAttribute("data-eo-note");
      const docAttr = el.getAttribute("data-eo-doc");
      const pageAttr = el.getAttribute("data-eo-page");
      const draftAttr = el.getAttribute("data-eo-draft");
      if (noteAttr) {
        e.preventDefault(); e.stopPropagation();
        const target = document.getElementById(`edocs-note-${noteAttr}`);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (draftAttr) {
        e.preventDefault(); e.stopPropagation();
        setSplitTarget({ type: "draft", id: draftAttr });
        setSplitOpen(true);
        return;
      }
      if (docAttr) {
        e.preventDefault(); e.stopPropagation();
        setSplitDocId(docAttr);
        setSplitDocPage(Number(pageAttr) || 1);
        setSplitTarget({ type: "doc", id: docAttr });
        setSplitOpen(true);
        return;
      }

      // 2) Legacy fragment-based anchors (older stored HTML)
      const href = el.getAttribute("href") || "";
      if (href.startsWith("#note-")) {
        e.preventDefault(); e.stopPropagation();
        const target = document.getElementById(`edocs-note-${href.replace("#note-", "")}`);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      const m = href.match(/^#doc:([^:]+):page=(\d+)$/);
      if (m) {
        e.preventDefault(); e.stopPropagation();
        setSplitDocId(m[1]);
        setSplitDocPage(Number(m[2]) || 1);
        setSplitOpen(true);
      }
    };
    document.addEventListener("click", handler, true);
    document.addEventListener("auxclick", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("auxclick", handler, true);
    };
  }, []);


  const commentsByBlock = useMemo(() => {
    const map = new Map();
    for (const c of (file?.comments || [])) {
      const key = c.block_id || "__file__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return map;
  }, [file?.comments]);

  if (!file) return <Layout><div className="p-10 text-[#5C5C54]">Loading...</div></Layout>;

  const finalizedBlocks = (file.blocks || []).filter((b) => b.finalized);
  const activeBlock = (file.blocks || []).find((b) => b.author_id === user?.id && !b.finalized);
  const otherActiveBlock = (file.blocks || []).find((b) => !b.finalized && b.author_id !== user?.id);
  const comments = file.comments || [];
  const openComments = comments.filter((c) => !c.resolved).length;
  const commentsForBlock = (bid) => commentsByBlock.get(bid) || [];
  const fileLevelComments = commentsByBlock.get("__file__") || [];

  return (
    <Layout>
      <div className={`grid grid-cols-1 ${splitOpen ? "lg:grid-cols-[1fr_1fr]" : ""} min-h-screen`}>
        {splitOpen && (
          <div data-testid="mobile-pane-toggle" className="lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex border border-[#1D3557] bg-white shadow-lg eo-no-print">
            {[{ k: "note", l: "Notesheet" }, { k: "doc", l: "Document" }].map((p) => (
              <button
                key={p.k}
                data-testid={`mobile-pane-${p.k}`}
                onClick={() => setMobilePane(p.k)}
                className={`px-5 h-10 text-[13px] font-semibold transition-colors duration-150 ${mobilePane === p.k ? "bg-[#1D3557] text-white" : "text-[#1D3557]"}`}
              >{p.l}</button>
            ))}
          </div>
        )}
        {/* Editor column */}
        <div className={`min-w-0 relative ${splitOpen && mobilePane === "doc" ? "hidden lg:block" : ""}`} data-eo-doc-panel="1">
          <div data-testid="editor-frozen-header" className="sticky top-[57px] z-20 bg-[#F5F5F0] shadow-[0_2px_6px_rgba(0,0,0,0.06)] eo-no-print">
          {/* Toolbar */}
          <div data-testid="editor-action-bar" className={`bg-[#FBFAF3] border-b border-[#DCD9D0] px-3 md:px-6 py-3 flex items-center gap-3 flex-nowrap overflow-x-auto eo-scroll [&>*]:shrink-0 ${splitOpen ? "" : "md:flex-wrap md:overflow-visible"}`}>
            <button onClick={() => nav(-1)} className="eo-btn-ghost flex items-center gap-1.5"><ArrowLeft size={14} /> Back</button>
            <div className="border-l border-[#DCD9D0] h-6 mx-1" />
            <div className="min-w-0">
              <div className="font-heading text-[18px] font-bold truncate max-w-[180px] md:max-w-[280px]">{file.name}</div>
              <div className="font-mono-eo text-[10px] text-[#8A8A80] uppercase tracking-widest">
                {file.file_no || `EDOCS/${(file.id||'').slice(0,8).toUpperCase()}`} · {file.approved ? "Closed" : "Draft"} · created {fmt(file.created_at)}
              </div>
            </div>
            <div className="flex-1" />
            {canEdit && (
              <button data-testid={EDITOR.saveBtn} onClick={() => saveBlock(activeHtml)} disabled={saving} className="eo-btn-ghost flex items-center gap-1.5">
                <Save size={14} /> {saving ? "Saving..." : "Save"}
              </button>
            )}
            {canEdit ? (
              <button data-testid={EDITOR.attachDocsBtn} onClick={() => setAttachOpen(true)} className="eo-btn-ghost flex items-center gap-1.5"><Files size={14} /> Attach ({(file.attached_docs || []).length})</button>
            ) : (
              <button
                data-testid={EDITOR.attachDocsBtn}
                disabled
                title={file?.approved ? "This file is closed." : "Only the current holder can attach documents. This file is with someone else right now."}
                className="eo-btn-ghost flex items-center gap-1.5 opacity-40 cursor-not-allowed"
              ><Files size={14} /> Attach ({(file.attached_docs || []).length})</button>
            )}
            <button data-testid={EDITOR.splitBtn} onClick={() => { setSplitOpen((v) => !v); if (!splitTarget && attachedDocs[0]) { setSplitDocId(attachedDocs[0].id); setSplitDocPage(1); setSplitTarget({ type: "doc", id: attachedDocs[0].id }); } }} className="eo-btn-ghost flex items-center gap-1.5"><PanelRightOpen size={14} /> Split View</button>
            <button data-testid="editor-comments-btn" onClick={() => { setCommentBlockId(null); setCommentsOpen(true); }} className="eo-btn-ghost flex items-center gap-1.5 relative">
              <MessageSquare size={14} /> Comments
              {openComments > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E63946] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full">{openComments}</span>}
            </button>
            <button data-testid={EDITOR.historyBtn} onClick={() => setHistoryOpen(true)} className="eo-btn-ghost flex items-center gap-1.5"><Clock size={14} /> Movement History</button>
            <button data-testid="editor-pdf-btn" onClick={onExportClick} disabled={exporting} className="eo-btn-ghost flex items-center gap-1.5"><FileDown size={14} /> {exporting ? "Exporting..." : "Export PDF"}</button>
            {canRevoke && (
              <button data-testid={EDITOR.revokeBtn} onClick={revoke} className="eo-btn-ghost flex items-center gap-1.5 border-[#E63946] text-[#E63946] hover:bg-[#FBECEE]"><Undo size={14} /> Revoke</button>
            )}
            {canDelete && (
              <button data-testid="editor-delete-btn" onClick={deleteFile} className="eo-btn-ghost flex items-center gap-1.5 border-[#E63946] text-[#E63946] hover:bg-[#FBECEE]"><Trash2 size={14} /> Delete draft</button>
            )}
            {canEdit && (
              <button data-testid={EDITOR.sendBtn} onClick={() => setSendOpen(true)} className="eo-btn-primary flex items-center gap-1.5"><Send size={14} /> Send</button>
            )}
            {canSendBack && (
              <button
                data-testid={EDITOR.sendBackBtn}
                onClick={openSendBack}
                title={`Send back to ${previousSender.name}`}
                className="eo-btn-ghost flex items-center gap-1.5 border-[#5C5C54] text-[#3A3A32] hover:bg-[#EFEEE3]"
              >
                <CornerUpLeft size={14} /> Send Back
              </button>
            )}
            {canApprove && (
              <button data-testid={EDITOR.approveBtn} onClick={openCloseDialog} className="eo-btn-primary flex items-center gap-1.5" style={{ background: "#2A9D8F", borderColor: "#2A9D8F" }}><CheckCircle2 size={14} /> Close file</button>
            )}
          </div>

          {/* Metadata banner — Department · Category · Refs · Remark. Hidden if none set (legacy files). */}
          {(file.department || file.category || file.remark || file.previous_reference || file.later_reference) && (
            <div className="max-w-[880px] mx-auto pt-3 px-3 md:px-6">
              <div data-testid="editor-metadata-banner" className="border border-[#DCD9D0] bg-[#F5F3E9] px-4 py-2 flex overflow-x-auto md:grid md:grid-cols-5 gap-3 [&>*]:min-w-[110px] md:[&>*]:min-w-0">
                {[
                  { label: "Department", value: file.department },
                  { label: "Category", value: file.category },
                  { label: "Prev Ref", value: file.previous_reference },
                  { label: "Later Ref", value: file.later_reference },
                  { label: "Remark", value: file.remark },
                ].map((f) => (
                  <div key={f.label} className="min-w-0">
                    <div className="font-mono-eo text-[9px] uppercase tracking-widest text-[#8A8A80]">// {f.label}</div>
                    <div className="text-[13px] mt-0.5 truncate" title={f.value || ""}>{f.value || <span className="text-[#B8B4A5]">—</span>}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canEdit && activeBlock && (
            <div className="max-w-[880px] mx-auto px-3 md:px-6 pt-3 pb-3">
              <div ref={setToolbarSlot} data-testid="editor-toolbar-slot" className="border border-[#DCD9D0] border-b-0 bg-[#FBFAF3]" />
            </div>
          )}
          {!(canEdit && activeBlock) && <div className="h-3" />}
          </div>

          {/* Document */}
          <div className="max-w-[880px] mx-auto py-6 md:py-10 px-3 md:px-6 pb-20 md:pb-10 relative">
            {lastSaved && canEdit && <div className="font-mono-eo text-[10px] text-[#8A8A80] mb-2 eo-no-print">Autosaved {fmt(lastSaved)}</div>}
            <div className="relative eo-editor-shell eo-shadow">
              {!file.approved && file.current_holder_id === user?.id && (<div className="eo-watermark"><span>DRAFT</span></div>)}
              <div className="relative z-[2]">
                {/* Finalized blocks (read-only) — noting-sheet format */}
                {(() => {
                  let noteCounter = 1;
                  return finalizedBlocks.map((b) => {
                    const bComments = commentsForBlock(b.id);
                    const stripped = (b.html || "").replace(/<[^>]*>/g, "").trim();
                    if (file.approved && !stripped) return null; // hide empty finalized blocks post-approval
                    const noteNum = noteCounter++;
                    const authorAttachments = (docsByBlock[b.id] || [])
                      .map((did) => documents.find((d) => d.id === did))
                      .filter(Boolean);
                    return (
                      <div key={b.id} id={`edocs-note-${noteNum}`} className="eo-block-locked">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="font-heading text-[15px] font-bold underline">Note # {noteNum}</div>
                          <button
                            onClick={() => { setCommentBlockId(b.id); setCommentsOpen(true); }}
                            className="text-[11px] font-mono-eo text-[#1D3557] flex items-center gap-1 hover:underline eo-no-print"
                            data-testid={`comment-block-${b.id}`}
                          >
                            <MessageSquare size={11} /> {bComments.length > 0 ? `${bComments.length} comment${bComments.length > 1 ? "s" : ""}` : "Add comment"}
                          </button>
                        </div>
                        <div dangerouslySetInnerHTML={safeHtml(b.html || "<p><em>(empty)</em></p>")} />
                        {authorAttachments.length > 0 && (
                          <div data-testid={`note-attachments-${b.id}`} className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {authorAttachments.map((d) => (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() => openDocInSplit(d.id, 1)}
                                data-testid={`note-attach-link-${b.id}-${d.id}`}
                                title={`Open ${d.original_filename || d.title} in split view`}
                                className="inline-flex items-center gap-1 text-[13px] text-[#1D3557] underline decoration-[#1D3557] underline-offset-2 hover:text-[#457B9D] hover:decoration-[#457B9D]"
                              >
                                <Paperclip size={12} /> {d.title || d.original_filename}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="eo-signature">
                          <div>{fmt(b.finalized_at || b.created_at)}</div>
                          <div className="text-right">
                            <div className="uppercase tracking-wider font-bold text-[#0A0A0A]">{b.author_name}</div>
                            <div className="font-bold text-[#0A0A0A]">{b.author_designation}</div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}

                {/* Approval note — text signature (no sticker) */}
                {file.approved && file.approved_at && (
                  <div key="approval-note" id={`edocs-note-${1 + finalizedBlocks.filter((b) => (b.html || "").replace(/<[^>]*>/g, "").trim() !== "").length}`} className="eo-block-locked">
                    <div className="font-heading text-[15px] font-bold underline mb-2">
                      Note # {1 + finalizedBlocks.filter((b) => (b.html || "").replace(/<[^>]*>/g, "").trim() !== "").length}
                    </div>
                    <p className="text-[16px]">
                      <b className="font-heading tracking-widest text-[#2A9D8F]">CLOSED</b>
                      {" "}— This document has been closed and finalized.
                    </p>
                    <div className="eo-signature">
                      <div>{fmt(file.approved_at)}</div>
                      <div className="text-right">
                        <div className="uppercase tracking-wider font-bold text-[#0A0A0A]">{file.creator_name}</div>
                        <div className="font-bold text-[#0A0A0A]">{file.creator_designation}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Editable / active block */}
                {canEdit && activeBlock ? (
                  <div>
                    {finalizedBlocks.length > 0 && (
                      <div className="px-5 md:px-16 pt-6 pb-0 font-mono-eo text-[10px] uppercase tracking-widest text-[#1D3557]">
                        // Your turn — {user?.name}, {user?.designation}
                      </div>
                    )}
                    <RichEditor
                      value={activeHtml}
                      onChange={setActiveHtml}
                      editable={true}
                      placeholder="Write your section here. Previous authors' text is locked above."
                      noteCount={finalizedNoteCount}
                      attachedDocs={attachedDocs}
                      fileDrafts={fileDrafts}
                      toolbarTarget={toolbarSlot}
                    />
                    {(docsByBlock[activeBlock?.id] || []).length > 0 && (
                      <div data-testid="active-note-attachments" className="mt-2 px-5 md:px-16 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {(docsByBlock[activeBlock?.id] || [])
                          .map((did) => documents.find((d) => d.id === did))
                          .filter(Boolean)
                          .map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => openDocInSplit(d.id, 1)}
                              data-testid={`active-attach-link-${d.id}`}
                              title={`Open ${d.original_filename || d.title} in split view`}
                              className="inline-flex items-center gap-1 text-[13px] text-[#1D3557] underline decoration-[#1D3557] underline-offset-2 hover:text-[#457B9D] hover:decoration-[#457B9D] eo-no-print"
                            >
                              <Paperclip size={12} /> {d.title || d.original_filename}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                ) : otherActiveBlock && !file.approved ? (
                  <div className="eo-block-locked">
                    <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mb-2">
                      // Currently with {otherActiveBlock.author_name} · {otherActiveBlock.author_designation}
                    </div>
                    <div dangerouslySetInnerHTML={safeHtml(otherActiveBlock.html || "<p class='text-[#8A8A80]'><em>Draft in progress...</em></p>")} />
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        </div>

        {/* Split view */}
        {splitOpen && (
          <div className={`border-l-2 border-[#B8B5AA] bg-[#FAFAFA] flex-col eo-no-print pb-16 lg:pb-0 ${mobilePane === "doc" ? "flex" : "hidden lg:flex"}`}>
            <div className="sticky top-[57px] z-20 bg-[#FBFAF3] border-b border-[#DCD9D0] px-3 md:px-4 py-3 flex items-center gap-2 flex-wrap">
              <select
                data-testid="split-doc-select"
                value={splitTarget ? `${splitTarget.type}:${splitTarget.id}` : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) { setSplitTarget(null); return; }
                  const [type, tid] = v.split(":");
                  if (type === "doc") { setSplitDocId(tid); setSplitDocPage(1); }
                  setSplitTarget({ type, id: tid });
                }}
                className="eo-input w-full md:w-auto md:max-w-xs"
              >
                <option value="">Select item to view</option>
                {attachedDocs.length > 0 && (
                  <optgroup label="Supporting documents (Depository)">
                    {attachedDocs.map((d) => (
                      <option key={d.id} value={`doc:${d.id}`}>{d.title} ({d.extension.toUpperCase()})</option>
                    ))}
                  </optgroup>
                )}
                {fileDrafts.length > 0 && (
                  <optgroup label="Draft documents (in this file)">
                    {fileDrafts.map((d) => (
                      <option key={d.id} value={`draft:${d.id}`}>{d.title || "Untitled draft"} · by {d.author_name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {splitTarget?.type === "doc" && (
                <div className="flex items-center gap-1 text-[11px] font-mono-eo text-[#5C5C54]">
                  <span>Page</span>
                  <input
                    type="number"
                    min="1"
                    value={splitDocPage}
                    onChange={(e) => setSplitDocPage(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-14 border border-[#DCD9D0] bg-white h-7 px-1.5 text-[12px]"
                    data-testid="split-page-input"
                  />
                </div>
              )}
              {splitTarget?.type === "draft" && (
                <span className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A6A00] bg-[#FFF5D6] border border-[#EBD79F] px-2 py-1">
                  {draftSaveState === "saving" ? "Saving…" : draftSaveState === "dirty" ? "Unsaved" : "Saved"}
                </span>
              )}
              <div className="flex-1" />
              {canEdit && (
                <button
                  data-testid="split-new-draft-btn"
                  onClick={createDraft}
                  className="eo-btn-ghost text-[12px] flex items-center gap-1.5"
                  title="Create a new draft document that travels with this file"
                >
                  <Pencil size={12} /> New draft
                </button>
              )}
              <button
                data-testid="split-upload-btn"
                onClick={() => setSplitUploadOpen(true)}
                className="eo-btn-ghost text-[12px] flex items-center gap-1.5"
                title="Upload a new supporting document from your computer"
              >
                <Files size={12} /> Upload & attach
              </button>
              {splitTarget?.type === "doc" && (
                <a href={docPreviewUrl(splitTarget.id)} target="_blank" rel="noreferrer" className="eo-btn-ghost text-[12px] flex items-center gap-1.5"><Download size={12} /> Download</a>
              )}
              {splitTarget?.type === "draft" && currentDraft && currentDraft.author_id === user?.id && (
                <button
                  onClick={() => deleteDraft(currentDraft.id)}
                  className="eo-btn-ghost text-[12px] flex items-center gap-1.5 text-[#E63946]"
                  title="Delete this draft"
                >
                  <Trash2 size={12} /> Delete
                </button>
              )}
              <button onClick={() => setSplitOpen(false)} className="eo-btn-ghost" data-testid="split-close-btn"><X size={14} /></button>
            </div>
            <div className="flex-1 min-h-0" style={{ background: splitTarget?.type === "draft" ? "#FFFDF5" : "#FAFAFA" }}>
              {splitTarget?.type === "draft" && currentDraft ? (
                canEdit ? (
                  <div className="h-full flex flex-col p-4">
                    <input
                      data-testid="draft-title-input"
                      value={draftTitle}
                      onChange={(e) => { setDraftTitle(e.target.value); setDraftSaveState("dirty"); }}
                      placeholder="Draft title"
                      className="w-full text-[22px] font-heading font-bold bg-transparent border-b border-[#EBD79F] pb-2 mb-3 focus:outline-none focus:border-[#8A6A00]"
                    />
                    <div className="flex-1 min-h-0 border border-[#EBD79F] bg-[#FFFDF5] overflow-auto" data-testid="draft-editor-wrap">
                      <RichEditor
                        value={draftHtml}
                        onChange={(html) => { setDraftHtml(html); setDraftSaveState("dirty"); }}
                        editable={true}
                        placeholder="Write your draft here. It travels with the file to the next holder as an attachment."
                      />
                    </div>
                    <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#5C5C54] mt-2">
                      // In-file draft · by {currentDraft.author_name} · created {fmt(currentDraft.created_at)}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 max-w-4xl mx-auto">
                    <div className="font-heading text-[22px] font-bold mb-1">{currentDraft.title}</div>
                    <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mb-4">// by {currentDraft.author_name} · {fmt(currentDraft.created_at)}</div>
                    <div className="prose max-w-none" dangerouslySetInnerHTML={safeHtml(currentDraft.html || "<p><em>(empty)</em></p>")} />
                  </div>
                )
              ) : splitTarget?.type === "doc" && splitTarget.id ? (
                <DocViewer doc={documents.find((d) => d.id === splitTarget.id)} page={splitDocPage} />
              ) : (
                <div className="p-10 text-center text-[#8A8A80] text-[13px]">
                  {(attachedDocs.length === 0 && fileDrafts.length === 0) ? (
                    <>No supporting documents or drafts on this file yet.<br />Use <b>Attach</b>, <b>Upload &amp; attach</b>, or <b>New draft</b> to add one.</>
                  ) : (
                    <>Select an attached document or draft from the dropdown above to view it here.</>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Export PDF dialog — shown ONLY when a closed file has attachments */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-[520px]">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Export PDF</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-[13px] text-[#5C5C54]">
              This file has <b>{attachedCount}</b> supporting document{attachedCount === 1 ? "" : "s"} attached. Choose what to include in the PDF:
            </p>
            <button
              type="button"
              data-testid="export-single-btn"
              onClick={() => exportPdf("single")}
              disabled={exporting}
              className="w-full text-left border border-[#DCD9D0] bg-[#FBFAF3] hover:bg-[#F5F3E9] px-4 py-3 transition-colors"
            >
              <div className="flex items-start gap-3">
                <FileDown size={18} className="mt-0.5 text-[#1D3557]" />
                <div className="min-w-0">
                  <div className="font-heading font-bold text-[14px]">Notesheet only (without attachments)</div>
                  <div className="text-[12px] text-[#5C5C54] mt-0.5">{file?.approved ? "The noting sheet with the CLOSED seal." : "The draft noting sheet with the DRAFT watermark."} No attachments.</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              data-testid="export-bundle-btn"
              onClick={() => exportPdf("bundle")}
              disabled={exporting}
              className="w-full text-left border border-[#1D3557] bg-[#F5F3E9] hover:bg-[#EFEEE3] px-4 py-3 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Files size={18} className="mt-0.5 text-[#1D3557]" />
                <div className="min-w-0">
                  <div className="font-heading font-bold text-[14px]">Notesheet + hyperlinked attachments</div>
                  <div className="text-[12px] text-[#5C5C54] mt-0.5">Each note is followed by every supporting document that note links to. Any remaining attachments are appended at the end. Word docs are auto-converted.</div>
                </div>
              </div>
            </button>
            {exporting && <p className="text-[11px] text-[#8A8A80] font-mono-eo">Generating PDF — this may take a few seconds for large bundles...</p>}
          </div>
          <DialogFooter>
            <button onClick={() => setExportOpen(false)} className="eo-btn-ghost">Cancel</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close-file dialog with promote-drafts checkbox list */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-[560px]">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Close this file</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-[13px] text-[#5C5C54]">Once closed, no further edits are allowed. The CLOSED seal is stamped and the file moves to your Closed tab.</p>
            {(file?.file_drafts || []).length > 0 && (
              <div className="border border-[#EBD79F] bg-[#FFFDF5] px-4 py-3">
                <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A6A00] mb-2">// Promote drafts to Depository</div>
                <p className="text-[12px] text-[#5C5C54] mb-3">Selected drafts will be converted to a PDF and stored in the Depository as proper supporting documents, then auto-attached to this file.</p>
                <div className="space-y-1.5">
                  {(file?.file_drafts || []).map((d) => (
                    <label key={d.id} className="flex items-center gap-2 text-[13px]">
                      <input
                        type="checkbox"
                        checked={promoteDraftIds.includes(d.id)}
                        onChange={(e) => setPromoteDraftIds((s) => e.target.checked ? [...s, d.id] : s.filter((x) => x !== d.id))}
                        data-testid={`promote-draft-${d.id}`}
                      />
                      <span className="truncate"><b>{d.title || "Untitled draft"}</b> <span className="text-[#8A8A80]">· by {d.author_name}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setCloseOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid="confirm-close-btn" onClick={approve} className="eo-btn-primary" style={{ background: "#2A9D8F", borderColor: "#2A9D8F" }}>Close file</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Send file to user</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {previousSender && (
              <div className="border border-[#DCD9D0] bg-[#F5F3E9] px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">Quick action</div>
                  <div className="text-[13px] truncate">
                    Send back to <b>{previousSender.name}</b>
                    {previousSender.designation ? <span className="text-[#5C5C54]"> — {previousSender.designation}</span> : null}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={EDITOR.sendDialogQuickBack}
                  onClick={() => setSendTo(previousSender.id)}
                  className="eo-btn-ghost flex items-center gap-1.5 border-[#5C5C54] text-[#3A3A32] hover:bg-[#EFEEE3] whitespace-nowrap"
                >
                  <CornerUpLeft size={14} /> Send Back
                </button>
              </div>
            )}
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Recipient</label>
              <select data-testid={EDITOR.sendDialogUser} value={sendTo} onChange={(e) => setSendTo(e.target.value)} className="eo-input">
                <option value="">Select user</option>
                {users.filter((u) => u.id !== user?.id).map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — {u.designation}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Note (optional)</label>
              <textarea data-testid={EDITOR.sendDialogMessage} value={sendMsg} onChange={(e) => setSendMsg(e.target.value)} className="eo-input min-h-[80px]" placeholder="Any remarks for the recipient..." />
            </div>
            <p className="text-[11px] text-[#8A8A80] font-mono-eo">Once the recipient opens the file, you will no longer be able to revoke or edit your section.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setSendOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid={EDITOR.sendDialogConfirm} onClick={send} className="eo-btn-primary">Send</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split-view upload dialog — upload a new depository doc and auto-attach it to this file */}
      <Dialog open={splitUploadOpen} onOpenChange={setSplitUploadOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Upload &amp; attach supporting document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Title</label>
              <input data-testid="split-upload-title" className="eo-input" value={splitUploadTitle} onChange={(e) => setSplitUploadTitle(e.target.value)} placeholder={splitUploadFile?.name || "Document title"} />
            </div>
            <div>
              <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">File (PDF or Word only)</label>
              <input data-testid="split-upload-input" type="file" accept=".pdf,.docx,.doc" onChange={(e) => setSplitUploadFile(e.target.files?.[0])} className="eo-input" />
            </div>
            <p className="text-[11px] text-[#8A8A80] font-mono-eo">The document will be added to the shared depository (visible to everyone) and immediately attached to this file for split-view reference.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setSplitUploadOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid="split-upload-submit" disabled={splitUploadBusy || !splitUploadFile} onClick={splitUpload} className="eo-btn-primary">{splitUploadBusy ? "Uploading..." : "Upload & attach"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach dialog */}
      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Attach supporting documents</DialogTitle></DialogHeader>
          <div className="border border-[#DCD9D0] bg-[#FBFAF3] p-3 mb-3">
            <div className="text-[11px] font-mono-eo uppercase tracking-widest text-[#8A8A80] mb-2">// Upload a new document to the depository</div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => setSplitUploadFile(e.target.files?.[0])}
                className="eo-input py-1 px-2 text-[12px] flex-1 min-w-[200px]"
                data-testid="attach-upload-input"
              />
              <input
                placeholder="Title (optional)"
                value={splitUploadTitle}
                onChange={(e) => setSplitUploadTitle(e.target.value)}
                className="eo-input py-1 px-2 text-[12px] w-[220px]"
                data-testid="attach-upload-title"
              />
              <button
                data-testid="attach-upload-submit"
                disabled={!splitUploadFile || splitUploadBusy}
                onClick={splitUpload}
                className="eo-btn-primary text-[12px] flex items-center gap-1.5"
              >
                <Files size={12} /> {splitUploadBusy ? "Uploading..." : "Upload & attach"}
              </button>
            </div>
            <p className="text-[10px] text-[#8A8A80] mt-2 font-mono-eo">Uploaded files are added to the shared depository and immediately attached to this file.</p>
          </div>
          <div className="max-h-[420px] overflow-auto eo-scroll border border-[#DCD9D0]">
            {documents.length === 0 && <div className="p-6 text-[13px] text-[#8A8A80]">No documents in the depository yet. Upload one above to attach it here.</div>}
            {documents.map((d) => {
              const checked = selectedTargets.includes(d.id);
              const meta = (file?.attachment_meta || []).find((m) => m.doc_id === d.id);
              const attacherId = meta?.attached_by || (file?.attached_docs?.includes(d.id) ? file?.creator_id : null);
              const currentlyAttached = (file?.attached_docs || []).includes(d.id);
              const detachable = (file?.detachable_doc_ids || []).includes(d.id);
              // The user can toggle a row if: (a) it isn't currently attached
              // (so they'd be ADDING it), or (b) they're allowed to detach it
              // right now (own doc, own current session). Once a note has been
              // sent, attachments from that note are locked for everyone.
              const canToggle = !currentlyAttached || detachable;
              const attacher = attacherId ? (users.find((u) => u.id === attacherId)) : null;
              const isSelfPriorSession = currentlyAttached && !detachable && attacherId === user?.id;
              return (
                <label key={d.id} className={`flex items-start gap-3 px-4 py-3 border-b border-[#DCD9D0] ${canToggle ? "cursor-pointer hover:bg-[#FBFAF3]" : "opacity-70 cursor-not-allowed bg-[#F5F3E9]"}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!canToggle}
                    onChange={(e) => setSelectedTargets((s) => e.target.checked ? [...s, d.id] : s.filter((x) => x !== d.id))}
                    className="mt-1"
                    data-testid={`attach-check-${d.id}`}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-[14px]">{d.title}</div>
                    <div className="text-[11px] font-mono-eo text-[#8A8A80]">{d.extension.toUpperCase()} · {(d.size / 1024).toFixed(1)} KB · by {d.uploader_name}</div>
                    {d.description && <div className="text-[12px] text-[#5C5C54] mt-1">{d.description}</div>}
                    {currentlyAttached && !canToggle && !isSelfPriorSession && (
                      <div className="text-[11px] text-[#8A8A80] mt-1 italic" data-testid={`attach-locked-other-${d.id}`}>
                        Locked — attached by {attacher?.name || "another user"} in a previous note. It stays on the file permanently.
                      </div>
                    )}
                    {isSelfPriorSession && (
                      <div className="text-[11px] text-[#8A8A80] mt-1 italic" data-testid={`attach-locked-self-${d.id}`}>
                        Locked — you attached this in a previous note. You can add new attachments for the note you're writing now, but earlier ones can't be removed.
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <button onClick={() => setAttachOpen(false)} className="eo-btn-ghost">Cancel</button>
            <button
              onClick={saveAttached}
              disabled={!canEdit}
              title={canEdit ? undefined : "Only the current holder can attach documents."}
              className="eo-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="attach-save-btn"
            >Save attachments</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comments dialog */}
      <Dialog open={commentsOpen} onOpenChange={setCommentsOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px]">
              {commentBlockId ? "Section comments" : "All comments"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[440px] overflow-auto eo-scroll">
            {/* When a specific block is targeted, show only that block's comments; otherwise all */}
            {(commentBlockId ? commentsForBlock(commentBlockId) : comments).length === 0 && (
              <div className="p-6 text-[13px] text-[#8A8A80] border border-[#DCD9D0]">No comments yet.</div>
            )}
            {(commentBlockId ? commentsForBlock(commentBlockId) : comments).map((c) => (
              <div
                key={c.id}
                data-testid={`comment-${c.id}`}
                className={`border border-[#DCD9D0] p-4 mb-2 ${c.resolved ? "bg-[#F0EFE7] opacity-70" : "bg-white"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold">{c.author_name} <span className="text-[#8A8A80] font-normal text-[12px]">· {c.author_designation}</span></div>
                    <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80] mt-0.5">{fmt(c.created_at)}{c.block_id ? " · on a section" : " · file-level"}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleResolve(c.id, c.resolved)}
                      className="eo-btn-ghost text-[11px] flex items-center gap-1"
                      data-testid={`resolve-${c.id}`}
                    >
                      <Check size={11} /> {c.resolved ? "Reopen" : "Resolve"}
                    </button>
                    {(c.author_id === user?.id || user?.role === "admin") && (
                      <button onClick={() => deleteComment(c.id)} className="eo-btn-ghost text-[11px] border-[#E63946] text-[#E63946]" data-testid={`delete-comment-${c.id}`}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-[14px] mt-2 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t border-[#DCD9D0]">
            <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">
              Add {commentBlockId ? "section" : "file"} comment
            </label>
            <textarea
              data-testid="comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="eo-input min-h-[70px]"
              placeholder="Write a comment..."
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setCommentsOpen(false)} className="eo-btn-ghost">Close</button>
              <button data-testid="comment-submit-btn" onClick={addComment} disabled={!newComment.trim()} className="eo-btn-primary">Post</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Movement History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="border-[#DCD9D0] rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[22px]">Movement History</DialogTitle></DialogHeader>
          <div className="max-h-[500px] overflow-auto eo-scroll">
            <ol className="relative border-l border-[#DCD9D0] ml-3">
              {(file.history || []).map((h) => (
                <li key={h.id} className="mb-6 ml-4">
                  <span className="absolute -left-1.5 mt-1 w-3 h-3 bg-[#1D3557]" style={{ borderRadius: 2 }} />
                  <div className="font-mono-eo text-[11px] text-[#8A8A80] uppercase tracking-widest">{fmt(h.timestamp)} · {h.type}</div>
                  <div className="text-[14px] mt-1">
                    <b>{h.actor_name}</b> <span className="text-[#8A8A80]">({h.actor_designation})</span>
                    {h.type === "sent" && <> sent to <b>{h.target_name}</b> <span className="text-[#8A8A80]">({h.target_designation})</span></>}
                    {h.type === "revoked" && <> revoked from <b>{h.target_name}</b></>}
                    {h.type === "opened" && <> opened the file</>}
                    {h.type === "approved" && <> closed the file</>}
                    {h.type === "created" && <> created the file</>}
                  </div>
                  {h.message && <div className="text-[12px] text-[#5C5C54] mt-1 italic">"{h.message}"</div>}
                </li>
              ))}
            </ol>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function DocViewer({ doc, page = 1 }) {
  if (!doc) return null;
  const inlineUrl = docPreviewUrl(doc.id, { inline: true });
  const downloadUrl = docPreviewUrl(doc.id);
  const ext = doc.extension.toLowerCase();
  if (ext === "pdf") {
    // PDF viewers honor the #page=N fragment to jump to a specific page
    const src = `${inlineUrl}#page=${page || 1}&toolbar=1&navpanes=0&view=FitH`;
    return <iframe key={`${doc.id}-${page}`} title={doc.title} src={src} className="w-full h-full border-0" />;
  }
  return (
    <div className="p-8 text-center">
      <div className="font-heading font-bold text-[20px]">{doc.title}</div>
      <div className="font-mono-eo text-[11px] text-[#8A8A80] mt-1 uppercase tracking-widest">{ext.toUpperCase()} — inline preview not available in the browser</div>
      <a href={downloadUrl} target="_blank" rel="noreferrer" className="eo-btn-primary inline-flex items-center gap-2 mt-4"><Download size={14} /> Download to view</a>
    </div>
  );
}
